import type { ComponentsOverrides, PaletteColor, Theme } from '@mui/material';
import { filledInputClasses } from '@mui/material/FilledInput';
// eslint-disable-next-line no-restricted-imports
import { createTheme, useTheme } from '@mui/material/styles';
import { OverridesStyleRules } from 'node_modules/@mui/material/esm/styles/overrides';

import {
  ChevronDownIcon,
  InformationCircleIcon,
} from '~/shared/components/Icons/Icons';
import FSKimMediumOtf from '~/shared/theme/internal/fonts/fskim/FSKim-Medium.otf';
import FSKimRegularOtf from '~/shared/theme/internal/fonts/fskim/FSKim-Regular.otf';
import { createShadows } from '~/shared/theme/internal/shadows';

const supportiveMainRgb = '65, 64, 66';

// This is the only workaround to be able to modify root styling of MuiSelect
// https://github.com/mui/material-ui/issues/30225
type OverridesWithRoot = ComponentsOverrides<Theme>['MuiSelect'] & {
  root: OverridesStyleRules<'root', 'MuiSelect', Theme>['root'];
};

const bodyS = {
  fontSize: 16,
  fontWeight: 400,
  lineHeight: '140%',
};

const bodyXS = {
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '140%',
};

export const brokerValuesStyles = {
  fontSize: 12,
  muiChip: {
    '& .MuiChip-root': {
      fontSize: 12,
      height: 20,
    },
  },
  muiInput: {
    '& .MuiInputBase-input': {
      // !important is needed to override MUI styles.
      fontSize: '12px !important',
    },
  },
};

export const notificationStyles = {
  activeFilterBackground: 'primary.dark',
  filterBackground: '#FAFAFA',
  hoverColor: '#4D5180',
  darkText: '#0A0A0A',
  lightText: '#525252',
  filterBorderColor: '#E5E5E5',
  badgeColor: '#F54F46',
  settings: {
    borderColor: '#D3D9EA',
  },
  types: {
    notification: {
      backgroundColor: '#F54F46',
      color: '#FFFFFF',
    },
    alert: {
      backgroundColor: '#F54F46',
      color: '#FFFFFF',
    },
    reminder: {
      backgroundColor: '#FC9D34',
      color: '#FFFFFF',
    },
    solved: {
      backgroundColor: '#01A567',
      color: '#FFFFFF',
    },
    news: {
      backgroundColor: '#00A8E1',
      color: '#FFFFFF',
    },
    updates: {
      backgroundColor: '#01A567',
      color: '#FFFFFF',
    },
    resolved: {
      backgroundColor: '#E5E5E5',
      color: '#9596A2',
    },
  },
};

export const MIN_PAGE_HEIGHT_XS = '1312px';
export const MIN_PAGE_HEIGHT_LG = '1024px';

// Disabled to allow shadowing of `theme` for style overrides
/* eslint-disable @typescript-eslint/no-shadow */
export const theme = createTheme({
  spacing: 4,
  palette: {
    background: {
      paper: '#FFFFFF',
      default: '#F5F5F5',
    },
    primary: {
      dark: '#353750',
      main: '#65677A',
      light: '#9596A2',
      lightest: '#C5C5CC',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#00A8E1',
      lightest: '#B9E9FE',
      contrastText: '#FFFFFF',
    },
    supportive: {
      dark: '#414042',
      main: '#6E6D6F',
      light: '#A09fA0',
      lightest: '#CFCFD0',
    },
    error: {
      main: '#FF7D5E',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#FDBA63',
    },
    info: {
      main: '#62AF81',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#62AF81',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: '#353750',
      secondary: '#9596A2',
      disabled: '#C5C5CC',
    },
    divider: `rgba(${supportiveMainRgb}, .12)`,
    action: {
      active: `rgba(${supportiveMainRgb}, .54)`,
      hover: `rgba(${supportiveMainRgb}, .1)`,
      selected: `rgba(${supportiveMainRgb}, .08)`,
      disabled: `rgba(${supportiveMainRgb}, .26)`,
      disabledBackground: `rgba(${supportiveMainRgb}, .12)`,
      focus: `rgba(${supportiveMainRgb}, .12)`,
    },
  },
  border: {
    size: 1,
    radius: 10,
  },
  shadows: createShadows(supportiveMainRgb),
  typography: {
    fontFamily: 'tondo',
    allVariants: {
      color: '#353750',
    },
    button: {
      textTransform: 'none',
    },
    'display-xl': {
      fontFamily: 'FS Kim',
      fontSize: 100,
      fontWeight: 500,
      lineHeight: '100%',
      letterSpacing: '-3%',
    },
    'display-l': {
      fontFamily: 'FS Kim',
      fontSize: 80,
      fontWeight: 500,
      lineHeight: '100%',
      letterSpacing: '-1.5%',
    },
    'display-m': {
      fontFamily: 'FS Kim',
      fontSize: 60,
      fontWeight: 500,
      lineHeight: '110%',
      letterSpacing: '-1.5%',
    },
    'display-s': {
      fontFamily: 'FS Kim',
      fontSize: 50,
      fontWeight: 500,
      lineHeight: '110%',
      letterSpacing: '-1.5%',
    },
    'display-xs': {
      fontFamily: 'FS Kim',
      fontSize: 30,
      fontWeight: 400,
      lineHeight: '130%',
      letterSpacing: '-3%',
    },
    'heading-xl': {
      fontSize: 40,
      fontWeight: 300,
      lineHeight: '130%',
    },
    'heading-l': {
      fontSize: 36,
      fontWeight: 300,
      lineHeight: '130%',
    },
    'heading-m': {
      fontSize: 28,
      fontWeight: 300,
      lineHeight: '130%',
    },
    'body-xl': {
      fontSize: 24,
      fontWeight: 400,
      lineHeight: '140%',
    },
    'body-l': {
      fontSize: 20,
      fontWeight: 400,
      lineHeight: '140%',
    },
    'body-m': {
      fontSize: 18,
      fontWeight: 400,
      lineHeight: '140%',
    },
    'body-s': bodyS,
    body1: bodyS,
    'body-xs': bodyXS,
    body2: bodyXS,
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          'display-xl': 'h1',
          'display-l': 'h1',
          'display-m': 'h1',
          'display-s': 'h1',
          'display-xs': 'h2',

          'heading-xl': 'span',
          'heading-l': 'span',
          'heading-m': 'span',

          'body-xl': 'p',
          'body-l': 'p',
          'body-m': 'p',
          'body-s': 'p',
          'body-xs': 'p',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'FS Kim';
          font-weight: 500;
          src: local('FS Kim Medium'), url(${FSKimMediumOtf});
        }
        @font-face {
          font-family: 'FS Kim';
          font-weight: 400;
          src: local('FS Kim Regular'), url(${FSKimRegularOtf});
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.spacing(6),
          boxShadow: 'none',
          alignSelf: 'center',
          whiteSpace: 'nowrap',
          '&:hover': {
            boxShadow: 'none',
          },
        }),
        sizeLarge: ({ theme }) => ({
          padding: `${theme.spacing(4)} ${theme.spacing(6)}}`,
        }),
        sizeMedium: ({ theme }) => ({
          padding: `${theme.spacing(3)} ${theme.spacing(6)}}`,
        }),
        sizeSmall: ({ theme }) => ({
          padding: `${theme.spacing(2)} ${theme.spacing(6)}}`,
        }),
        contained: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.main,

          '&#delete-account-button': {
            backgroundColor: theme.palette.error.main,
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            },
          },
          '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
          },
        }),
        outlined: ({ theme }) => ({
          color: theme.palette.primary.dark,
          borderColor: theme.palette.primary.dark,
        }),
        text: ({ theme }) => ({
          color: theme.palette.primary.dark,
          textDecoration: 'underline',
          '&.filterButton': {
            textDecoration: 'none',
            padding: 0,
            '&:hover': {
              textDecoration: 'none',
              backgroundColor: 'transparent',
            },
          },
        }),
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: 300,
      },
      styleOverrides: {
        arrow: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
        tooltip: ({ theme }) => ({
          fontSize: theme.typography['body-xs'].fontSize,
          backgroundColor: theme.palette.text.primary,
        }),
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: ChevronDownIcon,
      },
      styleOverrides: {
        select: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.contrastText,
          border: 'none',
          width: '100%',
          padding: theme.spacing(4, 4, 4, 4),
          p: {
            maxWidth: theme.spacing(84),
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          },
        }),
        icon: ({ theme }) => ({
          fill: theme.palette.text.primary,
          fontSize: theme.spacing(4),
          right: theme.spacing(4),
        }),
        root: ({ theme }) =>
          theme.unstable_sx({
            outline: 'none',
            width: '100%',
            '.MuiOutlinedInput-notchedOutline': {
              borderRadius: theme.spacing(1.5),
              border: `1px solid ${theme.palette.primary.main}`,
            },
            '& .MuiNativeSelect-select': {
              border: 'none',
              borderRadius: theme.spacing(1.5),
            },
          }),
      } as OverridesWithRoot,
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiInputBase-root': {
            backgroundColor: theme.palette.background.paper,
            border: 'none',
            borderRadius: theme.spacing(3),
          },
          '& fieldset': {
            border: 'none',
          },
        }),
      },
    },
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '&&.MuiInputBase-root': {
            backgroundColor: theme.palette.background.default,
          },
          '&& .MuiSelect-select': {
            backgroundColor: theme.palette.background.default,
          },
          [`& .MuiInputBase-input.${filledInputClasses.sizeSmall}`]: {
            paddingTop: `${theme.spacing(2)}`,
            paddingBottom: `${theme.spacing(2)}`,
          },
          '& .MuiInputBase-input': {
            paddingTop: `${theme.spacing(4)}`,
            paddingBottom: `${theme.spacing(4)}`,
            borderRadius: theme.spacing(3),
          },
        }),
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: '100%',
          padding: theme.spacing(4, 6, 4, 6),
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          '&:not(:last-of-type)': {
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
          '&.Mui-selected': {
            fontWeight: theme.typography.fontWeightBold,
            backgroundColor: `${theme.palette.secondary.main} !important`,
            color: theme.palette.primary.contrastText,
          },
          '&:hover': {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
          },
        }),
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          success: <InformationCircleIcon />,
          error: <InformationCircleIcon />,
          info: <InformationCircleIcon />,
          warning: <InformationCircleIcon />,
        },
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          width: 'max-content',
          maxWidth: '100%',
          alignItems: 'center',
          borderRadius: theme.spacing(24),
          padding: theme.spacing(2, 6),
        }),

        message: ({ theme }) => ({
          fontSize: theme.typography['body-m'].fontSize,
          padding: 2,
        }),

        action: ({ theme }) => ({
          padding: 0,
          marginLeft: theme.spacing(4),
        }),

        standardInfo: ({ theme }) => ({
          color: theme.palette.primary.dark,
        }),

        standardSuccess: ({ theme }) => ({
          color: theme.palette.success.main,
        }),

        outlined: ({ theme }) => ({
          color: theme.palette.primary.dark,
          borderColor: theme.palette.primary.dark,

          '& .MuiAlert-icon': {
            color: theme.palette.primary.dark,
          },
        }),

        standardWarning: ({ theme }) => ({
          color: theme.palette.primary.dark,

          '& .MuiAlert-icon': {
            color: theme.palette.primary.dark,
          },
        }),
        standardError: ({ theme }) => ({
          color: theme.palette.error.main,
        }),
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme }) => ({
          boxShadow: theme.shadows[1],
          borderRadius: theme.border.radius,
        }),
      },
    },
  },
});

export type AppTheme = Theme & {
  palette: {
    primary: PaletteColor & { lightest: string };
    secondary: PaletteColor & { lightest: string };
    supportive: PaletteColor & { lightest: string };
  };
};

export function useAppTheme(): AppTheme {
  return useTheme<AppTheme>();
}
